import {EventHandler} from "@utility/eventHandler";
import {GameEvent} from "@game/gameEvent";
import {Vector2} from "@utility/vector2";
import {Geography} from "@game/map/geography";
import {MapSize} from "@game/map/mapSize";
import {Host} from "../host/host";
import {UserPlayer} from "@game/player/userPlayer";
import {Wordlist} from "@game/wordlist";
import {Letter} from "@game/map/letter";
import {Word} from "@game/word";
import {Player} from "@game/player/player";
import {Region} from "@game/map/region";
import {DataSubscriptionManager} from "@game/data/dataSubscriptionMangager";

interface IConfig {
    anonymousLoginPermitted: boolean
    maxShovels: number
}

export interface ILocation {
    regionId: number,
    tileId: number
}

export interface IPlacedLetter extends ILocation {
    letter: Letter
}

export interface IPlayerScore {
    count: number
    sum: number
}

export interface IGlobalScore {
    date: Date
    score: number
    player: string
}

export interface IWordScore {
    date: Date
    player: string
    regionId?: number
    score: number
    tileId?: number
    word: string
}

interface IPerks {
    noAdverts?: boolean
}
interface IPositionedLetter extends IPlacedLetter {
    position: Vector2
}

export class Game {
    private readonly _events = new EventHandler<GameEvent>();
    private readonly _subscriptionManager = new DataSubscriptionManager();
    private readonly _wordlist: Wordlist = new Wordlist({});
    private readonly _nicknames: Record<string, string> = {};

    private _config?: IConfig;
    private _errors: string[] = [];
    private _geography!: Geography;
    private _seed: number = 0;
    private _host!: Host;
    private _initialising = false;
    private _initialised = false;
    private _initialisationError?: Error;
    private _perks: IPerks = {};
    private _placed: IPositionedLetter[] = [];
    private _placedLegalWords: Word[] = [];
    private _placedIllegalWords: Word[] = [];
    private _placedIllegal: string[] = [];
    private _size!: MapSize;
    private _userPlayer?: UserPlayer;

    get anonymousLoginPermitted() {
        return this._config?.anonymousLoginPermitted ?? false;
    }

    get errors() {
        return this._errors;
    }

    get events() {
        return this._events;
    }

    get seed() {
        return this._seed;
    }

    get geography() {
        return this._geography;
    }

    get host() {
        return this._host;
    }

    get initialisationError() {
        return this._initialisationError;
    }
    
    get initialising() {
        return this._initialising;
    }

    get initialised() {
        return this._initialised;
    }

    get maxShovels() {
        return this._config?.maxShovels || 0;
    }

    get perks() {
        return this._perks;
    }

    get placed() {
        return this._placed;
    }

    get placedIllegal() {
        return this._placedIllegal;
    }

    get placedIllegalWords() {
        return this._placedIllegalWords;
    }

    get placedLegalWords() {
        return this._placedLegalWords;
    }

    get size() {
        return this._size;
    }

    get userPlayer() {
        return this._userPlayer
    }

    get wordlist() {
        return this._wordlist;
    }

    poll() {
        return this._subscriptionManager.poll();
    }

    async addInvite(host: string, username: string) {
        return this._host.cloud("addUserInvite", {host, username});
    }

    async createCompetition(name: string) {
        return this._host.cloud<{ id: string; inviteCode: string; endsAt: string }>("createCompetition", { name });
    }

    async joinCompetition(code: string) {
        return this._host.cloud<{ competitionId: string }>("joinCompetition", { code });
    }

    async getPlayerCompetitions() {
        interface ICompetition {
            id: string
            name: string
            invite_code: string
            ends_at: string
        }
        return this._host.cloud<{ results: ICompetition[] }>("getPlayerCompetitions");
    }

    async getCompetitionByCode(code: string) {
        return this._host.cloud<{ id: string; name: string; endsAt: string; memberCount: number }>(
            "getCompetitionByCode", { code }
        );
    }

    async getCompetitionStandings(id: string) {
        interface IStandingRow {
            nickname: string
            nick_number: number
            committed: number
            total: number
            best_word: string | null
            best_word_score: number | null
        }
        return this._host.cloud<{ results: IStandingRow[] }>("getCompetitionStandings", { id });
    }

    async getAchievements() {
        interface IAchievement {
            id: string
            name: string
            description: string
            unlocked: boolean
            unlockedAt: number | null
        }
        return this._host.cloud<{ results: IAchievement[] }>("getAchievements");
    }

    async getRegion(regionId: number) {
        interface ILetterData {
            letter: string
            placed: number
            player: string | null
        }
        interface IPlayerData {
            playerId: string
        }
        interface IGetRegion {
            letters: Record<string, ILetterData>
            places: Record<string, string>
            players: Record<string, IPlayerData>
        }

        return this._host.cloud<IGetRegion>("getRegion", {regionId});
    }

    async getUserGlobalHighScores() {
        interface IGlobalHighScore {
            player: string
            mean: number
            date: number
        }

        const leaderboard =
            await this._host.cloud<Record<string, IGlobalHighScore[]>>("getGlobalHighScores", {
                leaderboards: []
            }).then(results =>
                Object.fromEntries(Object.entries(results).map(([leaderboard, result]) =>
                    ([leaderboard, result.map(data => ({
                        date: new Date(data.date),
                        player: data.player,
                        score: data.mean
                    } as IGlobalScore))])))
            );
        const updateNicknames = Object.values(leaderboard).flat().map(result => result.player)
            .filter((value, index, array) => array.indexOf(value) === index)
            .filter(name => this._nicknames[name] === undefined);

        await this.getNicknames(updateNicknames);
        return leaderboard;
    }

    async getLeague(leagueId: string) {
        interface ILeagueRow {
            nickname: string
            nick_number: number
            committed: number
            total: number
            best_word: string | null
            best_word_score: number | null
        }
        interface ILeagueMeta { name: string; subtitle: string; description: string }
        const result = await this._host.cloud<{league: ILeagueMeta; results: ILeagueRow[]}>(
            "getLeague", { leagueId }
        );
        return { league: result.league, results: result.results ?? [] };
    }

    async getUserGlobalLeaderboards() {
        interface IGlobalLeaderboard {
            date: number
            player: string
            region_id?: number
            score: number
            tile_id?: number
            word: string
        }

        const leaderboard =
            await this._host.cloud<Record<string, IGlobalLeaderboard[]>>("getGlobalLeaderboards", {
                leaderboards: []
            }).then(results =>
                Object.fromEntries(Object.entries(results).map(([leaderboard, result]) =>
                    ([leaderboard, result.map(data => ({
                        ...data,
                        date: new Date(data.date),
                        regionId: data.region_id,
                        tileId: data.tile_id
                    } as IWordScore))])))
            );
        const updateNicknames = Object.values(leaderboard).flat().map(result => result.player)
            .filter((value, index, array) => array.indexOf(value) === index)
            .filter(name => this._nicknames[name] === undefined);

        await this.getNicknames(updateNicknames);
        return leaderboard;
    }

    async getUserScores() {
        interface IPlayerScores extends IPlayerScore {
            type: string
        }

        const scores = await this._host.cloud<IPlayerScores[]>("getPlayerScores", {
            playerId: this.userPlayer?.playerId
        });

        return Array.isArray(scores) ? Object.fromEntries(scores.map(score => [score.type, score])) : {};
    }

    getNickname(player: string) {
        return this._nicknames[player];
    }

    async getNicknames(playerIds: string[]) {
        interface IGetNickNames {
            playerId: string
            nickname: string
            nickNumber: number
        }

        if (playerIds.length == 0) return;

        const nicknames = await this._host.cloud<IGetNickNames[]>("getNicknames",
            {playerIds});

        nicknames.forEach((data: any) => {
            this._nicknames[data.playerId] = Player.MakeNickname(data.nickname, data.nickNumber);
        });
        this.events.update(new GameEvent("UpdatedNicknames"));
    }

    async getUserPlayerLeaderboards() {
        interface IPlayerLeaderboards {
            date: number
            region_id?: number
            score: number
            tile_id?: number
            word: string
        }

        return await this._host.cloud<Record<string, IPlayerLeaderboards[]>>("getPlayerLeaderboards", {
            leaderboards: []
        }).then(results =>
            Object.fromEntries(Object.entries(results).map(([leaderboard, result]) =>
            ([leaderboard, result.map(data => ({
                date: new Date(data.date),
                player: this.userPlayer?.playerId,
                regionId: data.region_id,
                score: data.score,
                tileId: data.tile_id,
                word: data.word
            } as IWordScore))])))
        );
    }

    async getWordsScore() {
        interface IWordScore {
            count: number
            score: number
        }

        return this._host.cloud<Record<string, IWordScore>>("getWordsScore", {
            words: this.placedLegalWords.map(word => ({
                placed: word.countPlacedLetters(this.userPlayer!.hand),
                word: word.getWordString()
            }))
        }).then(scores => {
            this.placedLegalWords.forEach(word => word.updateScore(scores[word.getWordString()].score));
            this.events.update(new GameEvent("WordScoresUpdated"))
        });
    }

    async initialise(host: Host) {
        this._initialising = true;
        try {
            this._host = host;
            this._config = await this.host.cloud<IConfig>("getConfig");
            await this.initialiseMap();
            await this._wordlist.initialise();
            if (! this.host.authenticated && this.anonymousLoginPermitted) {
                await this.host.logIn({});
            }
            if (this.host.authenticated) {
                await this.initialiseUserPlayer();
                if (this._userPlayer) {
                    this._nicknames[this._userPlayer?.playerId] = this._userPlayer?.getNickname();
                }
            }
            // Tokens may have expired and been cleared during initialiseUserPlayer — fall back to anonymous
            if (!this.host.authenticated && this.anonymousLoginPermitted) {
                await this.host.logIn({});
            }
            this._initialised = true;
            this._initialising = false;
            this.events.update(new GameEvent("Initialised"));
            this.host.cloud<IPerks>("getPlayerPerks").then(perks => {
                this._perks = perks;
                this.events.update(new GameEvent("PerksUpdated"));
            });
            await this._subscriptionManager.initialise(this);
        }
        catch (error: any) {
            this._initialising = false;
            this.throwError(`Error in initialisation: ${error.message}`);
        }
    }

    async initialiseUserPlayer() {
        interface IGetUserPlayer {
            hand: string []
            playerId: string
            location: { regionId: number, tileId: number }
            nickname: string
            nickNumber: number
            roles: string[]
            username: string
            score: number
            shovels: number
        }
        await this._host!.cloud<IGetUserPlayer>("getUserPlayer").then(userPlayer => {
            this._userPlayer = new UserPlayer({
                ...userPlayer,
                game: this
            });
            this.userPlayer?.events.delegates.add(event => this.handleUserPlayerUpdate(event))
            this._initialisationError = undefined;
            return this._userPlayer.initialise();
        }).then(() => this.events.update(new GameEvent("UserPlayerInitialised")))
            .catch(error => this._initialisationError = error);
    }

    async getUserRoles() {
        return await this._host!.cloud<string[]>("getUserRoles");
    }

    logIn(username: string, password: string) {
        return this.host.logIn({username, password});
    }

    logout() {
        this._host.logOut().then(async () => {
            this._userPlayer = undefined;
            if (this.anonymousLoginPermitted) {
                await this.host.logIn({});
                await this.initialiseUserPlayer();
            }
            this.updateAuthentication();
        });
    }

    placeLetter(letter: Letter, position: Vector2) {
        const regionId = this.size.getTileRegionId(position);
        const tileId = this.size.getRegionTileId(position);

        letter.placeOnMap(regionId, tileId);
        this._placed.push({letter, position, regionId, tileId});
        this.updatePlaced();
        this._placedIllegal.length &&
            console.log(`Illegal: ${this._placedIllegal.map(reason => reason).join(", ")}`);
    }

    async placeLetters(placed: Letter[]) {
        interface IHighScore {
            type: string
            player: string
            mean: number
            max: number
            min: number
        }
        interface ILeaderboard {
            count: number
            max: number
            min: number
            sum: number
        }
        interface IWord extends ILocation {
            count: number
            player: string
            score: number
            word: string
        }
        interface INewAchievement {
            id: string
            name: string
            description: string
        }
        interface IPlaceLetters {
            events: ILocation[]
            highScores: IHighScore[]
            leaderboard: Record<string, ILeaderboard>
            words: IWord[]
            hand: string[]
            score: number
            shovels: number
            newAchievements: INewAchievement[]
        }

        const result = await this._host.cloud<IPlaceLetters>("placeLetters", {
            letters: placed.map(letter => ({
                letter: letter.letter,
                tileId: letter.tileId,
                regionId: letter.regionId
            })),
            words: this.placedLegalWords.map(word => ({
                letters: word.letters.map(letter => ({
                    letter: letter.letter,
                    regionId: letter.regionId,
                    tileId: letter.tileId
                })),
                from: word.from as ILocation,
                to: word.to as ILocation,
                word: word.getWordString()
            }))
        }).then(result => {
            this.resetPlacedLetters();
            return result;
        });
        if (result?.words) {
            if (this.userPlayer?.hand) {
                this.userPlayer.hand.updateLetters(result.hand, true);
                this.userPlayer.hand.updateShovels(result.shovels);
                this.userPlayer.score.lifetime = result.score;
                this.userPlayer.hand.events.update(new GameEvent("Updated"));
            }
            this.events.update(new GameEvent("PlayerScored", result));
            if (result.newAchievements?.length) {
                this.events.update(new GameEvent("AchievementsUnlocked", { ids: result.newAchievements }));
            }
        }
    }

    removePlacedLetter(letter: Letter) {
        const index = this._placed.findIndex(placed => placed.letter === letter);

        if (index >= 0) {
            this._placed.splice(index, 1);
            this.updatePlaced();
            letter.removeFromMap();
        }
    }

    resetPlacedLetters() {
        this._placed.splice(0, this.placed.length);
        this._placedIllegal = [];
        this._placedIllegalWords = [];
        this._placedLegalWords = [];
    }

    async shovelTile(location: ILocation) {
        interface INewAchievement {
            id: string
            name: string
            description: string
        }
        interface IShovelTile {
            letter: string
            hand: string[]
            shovels: number
            newAchievements: INewAchievement[]
        }
        const result = await this._host.cloud<IShovelTile>("shovelTile", {location});

        const region = this.userPlayer?.nearbyRegions[location.regionId];
        if (region) {
            region.tiles[location.tileId]?.updateLetter(undefined);
        }
        this.events.update(new GameEvent("ShovelledTile", { hand: result.hand, shovels: result.shovels }));
        if (result.newAchievements?.length) {
            this.events.update(new GameEvent("AchievementsUnlocked", { ids: result.newAchievements }));
        }
    }

    updateAuthentication() {
        this.events.update(new GameEvent("UserPlayerUpdated"));
    }

    private throwError(message: string) {
        console.error(message);
        this._errors.push(message);
        this.events.update(new GameEvent("Error"));
        throw new Error(message);
    }

    private async handleUserPlayerUpdate(event: GameEvent) {
        if (event.type === "NearbyPlayersUpdated" && this.userPlayer) {
            const playerIds = (event.properties.playerIds as string[])
                .filter(playerId => this._nicknames[playerId] === undefined);

            await this.getNicknames(playerIds);
        }
    }

    private async initialiseMap() {
        interface IGetMapDetails {
            campDuration: number
            mapRegions: number[]
            regionTiles: number[]
            seed: number
        }
        const mapDetails = await this._host.cloud<IGetMapDetails>("getMapDetails");

        if (! mapDetails) {
            this.throwError("Failed to get map details");
        }
        this._size = new MapSize(mapDetails);
        this._seed = mapDetails.seed;
        this._geography = new Geography(this._size, mapDetails.seed);
    }

    private updatePlaced() {
        this._placedIllegal = [];
        this._placedLegalWords = [];
        this._placedIllegalWords = [];

        if (this._placed.every(letter => letter.position.x === this._placed[0].position.x) ||
            this._placed.every(letter => letter.position.y === this._placed[0].position.y)) {
            // All in a row
            this._placed.forEach(placed => this.updateWordsFromPlaced(placed));
            if (! this._placedLegalWords.some(word => word.letters.some(letter =>
                letter != this.userPlayer?.hand.dragging && ! this.userPlayer?.hand.placed.includes(letter)))) {
                this._placedIllegal.push(`At least one tile must be already in play`);
            }
            if (! this._placedLegalWords.some(word =>
                this._placed.every(placed => word.letters.includes(placed.letter))
            )) {
                this._placedIllegal.push(`Every placed tile must be in at least one legal word`);
            }
        } else {
            this._placedIllegal.push(`Every placed tile must be on the same row or column`);
        }
    }

    private updateWordsFromPlaced(placed: IPositionedLetter) {
        this.updateWordFromPlacedInDirection(placed, new Vector2(1, 0));
        this.updateWordFromPlacedInDirection(placed, new Vector2(0, 1));
    }

    private updateWordFromPlacedInDirection(placed: IPositionedLetter, direction: Vector2) {
        const getLetter = (location: ILocation) => {
            const placed = this._placed.find(placed =>
                placed.tileId === location.tileId && placed.regionId === location.regionId);

            if (placed) {
                return placed.letter;
            }

            const region = this._userPlayer?.nearbyRegions[location.regionId];

            if (region) {
                return region.tiles[location.tileId]?.letter;
            }
        }
        const reverseDirection = direction.multiply(-1);

        // Is this the first letter in the direction
        if (this._placed.length > 1 &&
            this._placed[0].position.minus(this._placed[1].position).normalise().abs().equals(direction) &&
            ! this._placed.every(test => test === placed || 
            test.position.minus(placed.position).normalise().equals(direction))) {
            return;
        }

        const letters = [placed.letter];
        let cursor: ILocation = this._size.getLocationOffset(placed, reverseDirection);
        let letter: Letter | undefined;
        let from: ILocation = placed;
        let to: ILocation = placed;

        while (letter = getLetter(cursor))
        {
            letters.unshift(letter);
            from = cursor;
            cursor = this._size.getLocationOffset(cursor, reverseDirection)
        }
        cursor = this._size.getLocationOffset(placed, direction);
        while (letter = getLetter(cursor))
        {
            letters.push(letter);
            to = cursor;
            cursor = this._size.getLocationOffset(cursor, direction)
        }
        if (letters.length > 1) {
            const text = letters.map(letter => letter.letter).join("");
            const word = new Word({
                from: {...from, position: this._size.getTilePosition(from)},
                to: {...to, position: this._size.getTilePosition(to)},
                letters: letters
            });

            if (this._wordlist.legal(text)) {
                this._placedLegalWords.push(word);
            } else {
                this._placedIllegalWords.push(word);
                this._placedIllegal.push(`${text} is not a legal word`);
            }
        }
    }
}
